import { useEffect, useMemo } from "react";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import useModuleAccess from "../../hooks/useModuleAccess";

// Single sidebar entry ("Inventory") -> this layout. A persistent tab bar
// provides navigation to every inventory section (no extra sidebar items).
// Each tab is its own deep-linkable route; the active tab follows the URL.
//
// `subModule` is the key from the backend module registry (inventory.subModules)
// that grants the tab. Tabs without one (Guide) are static help content and are
// always shown.
const TABS: { label: string; path: string; subModule?: string }[] = [
  { label: "Items", path: "/inventory", subModule: "items" },
  { label: "Stock", path: "/inventory/stock", subModule: "stock" },
  { label: "Locations", path: "/inventory/locations", subModule: "locations" },
  { label: "Goods Receipt", path: "/inventory/grn", subModule: "grn" },
  { label: "Batches", path: "/inventory/batches", subModule: "batches" },
  { label: "Requisitions", path: "/inventory/requisitions", subModule: "requisitions" },
  { label: "Transfers", path: "/inventory/transfers", subModule: "transfers" },
  { label: "Recipes", path: "/inventory/recipes", subModule: "recipes" },
  { label: "Open Containers", path: "/inventory/open-containers", subModule: "open-containers" },
  { label: "Variants", path: "/inventory/variants", subModule: "variants" },
  { label: "Guide", path: "/inventory/guide" },
];

const InventoryLayout = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { can } = useModuleAccess();

  const visibleTabs = useMemo(
    () => TABS.filter((tab) => !tab.subModule || can("inventory", "view", tab.subModule)),
    [can]
  );

  // Active tab = the tab whose path is the longest prefix of the current URL.
  // "Items" (/inventory) only matches exactly so deeper routes don't highlight it.
  const matchedValue = visibleTabs.reduce((best, tab) => {
    const isMatch = tab.path === "/inventory" ? pathname === "/inventory" : pathname.startsWith(tab.path);
    if (isMatch && tab.path.length > best.length) return tab.path;
    return best;
  }, "");

  const firstVisible = visibleTabs[0]?.path;

  // Landing on a tab the user can't see (usually /inventory itself) would just
  // fire 403s, so send them to the first tab they do have.
  useEffect(() => {
    if (!matchedValue && firstVisible && pathname !== firstVisible) {
      navigate(firstVisible, { replace: true });
    }
  }, [matchedValue, firstVisible, pathname, navigate]);

  if (!visibleTabs.length) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="body1" sx={{ color: "#666" }}>
          You do not have permission to view any Inventory section.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={matchedValue || firstVisible}
          onChange={(_e, value) => navigate(value)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {visibleTabs.map((tab) => (
            <Tab key={tab.path} label={tab.label} value={tab.path} sx={{ textTransform: "none", fontWeight: 600 }} />
          ))}
        </Tabs>
      </Box>
      <Box sx={{ pt: 1, pr: 2 }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default InventoryLayout;
